import MainPanelHeader from "../../layouts/mainPanelHeader";
import { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import "./style.scss";
import {
    COMPANY_CLUBS_SUMMARY,
    REPORT_BUBBLE,
    REPORT_CRITERIA_RANKING,
    REPORT_FEEDBACKS,
    REPORT_ITEM_RANKING,
    REPORT_PER_DAY,
    REPORT_SCORE_OVERVIEW,
} from "../../helperData/ApiAddress";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import ReportCard from "./components/ReportCard";
import OverviewKpiStrip from "./components/OverviewKpiStrip";
import StarRatings from "react-star-ratings";
import { useRequest } from "./../../hooks/helperData/useRequest";
import { Spin } from "antd";
import jMoment from "moment-jalaali";
import ItemTypeTreeModal from "./components/ItemTypeTreeModal";
import ChartFallback from "./components/ChartFallback";
import AiSuggestedActions from "./components/AiSuggestedActions";
import CriteriaScoreBreakdown from "./components/CriteriaScoreBreakdown";
import DBComingSoonModal from "../../component/global/DBComingSoonModal";
import { get } from "../../controlers/global/ConfigRequest";
import i18n from "../../i18n/i18n";
import useSetupSnapshot from "./hooks/useSetupSnapshot";
import {
    averageScoreFromOverview,
    countByStatus,
    cxFromScoreOverview,
    satisfactionPercentFromOverview,
    totalStatusCount,
} from "./setupUtils";

const STAR_BAR_COLORS = {
    5: "#0f766e",
    4: "#3d9a6a",
    3: "#d97706",
    2: "#ea580c",
    1: "#e11d48",
    0: "#94a3b8",
};

const ReportBar = ({ caption, value, percent, color }) => (
    <>
        <div>{Number(value) || 0}</div>
        <div className="report-bar">
            <div
                style={{
                    width: percent + "%",
                    backgroundColor: color || STAR_BAR_COLORS[caption],
                }}
            ></div>
        </div>
        <div>{caption}</div>
    </>
);

export default function Report() {
    const { t } = useTranslation();

    // const [companyList, setCompanyList] = useState([]);
    // const [currentCompany, setCurrentCompany] = useState([]);

    // useEffect(() => {
    //     if (window.sessionStorage.usertype == 1) {
    //         get(COMPANY_LIST_URL, {}).then((r) => {
    //             setCompanyList(
    //                 r.results.map((e) => {
    //                     return { name: e.name, value: e.id };
    //                 })
    //             );
    //             setCurrentCompany(
    //                 r.results.map((e) => {
    //                     return { name: e.name, value: e.id };
    //                 })[0].value
    //             );
    //         });
    //     } else {
    //         setCurrentCompany(window.sessionStorage.usercompany);
    //     }
    // }, []);

    const [reRenderKey, setRerenderKey] = useState(0);

    useEffect(() => {
        const setKey = (e) => setRerenderKey(e.target.innerWidth);

        window.addEventListener("resize", setKey, true);

        return () => window.removeEventListener("resize", setKey, true);
    }, []);

    const perDayTooltipCaption = (key) => {
        switch (key) {
            case "avg_score":
                return t("score");
            case "count":
                return t("feedback.count");
        }
    };

    const [perDayReq, perDayLoader] = useRequest(REPORT_PER_DAY, "GET");
    const [perDay, setPerDay] = useState([]);

    const getPerDay = () => {
        perDayReq()
            .then((r) => {
                if (!Array.isArray(r)) return;
                setPerDay(
                    r.map((item) => {
                        let jalali;

                        if (i18n.dir() === "rtl") {
                            jMoment.locale("fa");
                            jalali = jMoment(item.datetime__date).format(
                                "jDD jMMMM jYYYY"
                            );
                        } else {
                            jMoment.locale("en");
                            jalali = jMoment(item.datetime__date).format(
                                "DD MMMM YYYY"
                            );
                        }

                        return {
                            ...item,
                            datetime__date__j: jalali,
                        };
                    })
                );
            })
            .catch(() => {});
    };

    const [overviewReq, overviewLoader] = useRequest(
        REPORT_SCORE_OVERVIEW,
        "GET"
    );
    const [overview, setOverview] = useState({});

    const getOverview = () => {
        overviewReq()
            .then((r) => {
                setOverview(r);
            })
            .catch(() => {});
    };

    const [feedbackReq, feedbackLoader] = useRequest(REPORT_FEEDBACKS, "GET");
    const [feedback, setFeedback] = useState([]);

    const getFeedback = () => {
        feedbackReq()
            .then((r) => {
                setFeedback(r);
            })
            .catch(() => {});
    };

    const [bubbleReq, bubbleLoader] = useRequest(REPORT_BUBBLE, "GET");
    const [bubble, setBubble] = useState([]);

    const getBubble = (id) => {
        bubbleReq({ ...(id ? { parent: id } : { parent_isnull: true }) })
            .then((r) => {
                if (!Array.isArray(r)) return;
                setBubble(
                    r.map((item) => ({ ...item, exeWeight: item.weight * 500 }))
                );
            })
            .catch(() => {});
    };

    const [itemRankingReq, itemRankingLoader] = useRequest(
        REPORT_ITEM_RANKING,
        "GET"
    );
    const [itemRanking, setItemRanking] = useState([]);

    const getItemRanking = () => {
        itemRankingReq()
            .then((r) => {
                setItemRanking(
                    r.best.map((item, index) => ({
                        name: index + "",
                        best: item.computed_score + 1,
                        worst: (r.worst[index].computed_score + 1) * -1,
                        best_name: item.name,
                        worst_name: r.worst[index].name,
                    }))
                );
            })
            .catch(() => {});
    };

    const [criteriaReq, criteriaLoader] = useRequest(
        REPORT_CRITERIA_RANKING,
        "GET"
    );
    const [criteria, setCriteria] = useState([]);

    const getCriteria = () => {
        criteriaReq()
            .then((r) => {
                setCriteria(r);
            })
            .catch(() => {});
    };

    useEffect(() => {
        getPerDay();
        getOverview();
        getFeedback();
        getBubble();
        getItemRanking();
        getCriteria();
    }, []);

    const [bestWorst, setBestWorst] = useState("best");
    const [itemTypeTreeModalShow, setItemTypeTreeModalShow] = useState(false);
    const [comingSoon, setComingSoon] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [cx, setCx] = useState(null);
    const [cxLoading, setCxLoading] = useState(true);
    const [clubSummary, setClubSummary] = useState(null);

    const locale = i18n.language === "en" ? "en-US" : "fa-IR";
    const activeTab = searchParams.get("tab") === "ai" ? "ai" : "overview";

    useEffect(() => {
        setCxLoading(true);
        get(COMPANY_CLUBS_SUMMARY)
            .then((response) => {
                setCx(response?.cx || null);
                setClubSummary(response || null);
            })
            .catch(() => {
                setCx(null);
                setClubSummary(null);
            })
            .finally(() => setCxLoading(false));
    }, []);

    const selectTab = (key) => {
        const nextParams = new URLSearchParams(searchParams);
        if (key === "ai") {
            nextParams.set("tab", "ai");
        } else {
            nextParams.delete("tab");
        }
        setSearchParams(nextParams, { replace: true });
    };

    const totalMessages = totalStatusCount(feedback);
    const hasOverviewData = Number(overview.feedback_total_count) > 0;
    // Average of the star buckets in this card — not company.computed_score,
    // which is a separate hierarchical metric and can diverge badly.
    const overviewAverage = averageScoreFromOverview(overview);
    const displayScore =
        overviewAverage != null ? overviewAverage : "—";
    const bubbleHasData = bubble.some(
        (item) => Number(item.feedback_count) > 0
    );
    const setup = useSetupSnapshot(
        useMemo(
            () => ({
                messageCount: totalMessages,
                reviewCount: Number(overview.feedback_total_count) || 0,
            }),
            [overview.feedback_total_count, totalMessages]
        )
    );
    const overviewCx = useMemo(
        () => cxFromScoreOverview(overview),
        [overview]
    );
    const displayCx = cx || overviewCx;
    const newMessages = countByStatus(feedback, 1);
    const openMessages = countByStatus(feedback, 2);
    const processMessages = countByStatus(feedback, 3);
    const closedMessages = countByStatus(feedback, 4);
    const attentionMessages = newMessages + openMessages + processMessages;
    const pipeline = [
        { key: "new", label: t("new.messages"), value: newMessages, color: "#d97706" },
        { key: "open", label: t("open.messages"), value: openMessages, color: "#7c3aed" },
        { key: "process", label: t("on.process.messages"), value: processMessages, color: "#0f766e" },
        { key: "closed", label: t("closed.messages"), value: closedMessages, color: "#64748b" },
    ];

    const calculateOverviewPercent = (value) => {
        if (overview.feedback_total_count > 0) {
            return (value / overview.feedback_total_count) * 100;
        }
        return 0;
    };

    const BAR_GAP = 8;

    return (
        <div className="report-page main_panel_box">
            <DBComingSoonModal
                show={comingSoon}
                onHide={() => setComingSoon(false)}
                description={t("coming.soon")}
            />
            <ItemTypeTreeModal
                show={itemTypeTreeModalShow}
                onHide={() => setItemTypeTreeModalShow(false)}
                onChange={(id) => {
                    getBubble(id);
                    setItemTypeTreeModalShow(false);
                }}
            />
            <MainPanelHeader
                panelType={t("Corpration.Panel.label")}
                panelName={t("reports.title")}
            />
            <div className="main_panel_content">
                <div className="right_content main_box">
                    <div className="report-tabs tab-section">
                <Tabs
                    activeKey={activeTab}
                    transition={false}
                    className="panel_tab"
                    onSelect={(key) => selectTab(key || "overview")}
                >
                    <Tab
                        eventKey="overview"
                        className="panel_tab_item"
                        title={t("reports.title")}
                    >
            <OverviewKpiStrip
                cx={displayCx}
                locale={locale}
                t={t}
                cxLoading={cxLoading}
                messagesLoading={feedbackLoader}
                participation={{
                    total: totalMessages,
                    attention: attentionMessages,
                    ideas: Number(clubSummary?.ideas) || 0,
                    referrals: Number(clubSummary?.referral_opens) || 0,
                }}
            />
            <div className="insight-row">
                <ReportCard className="score-card">
                    <div className="overview-info">
                        <h1 className="report-header">{t("overview")}</h1>
                        {overviewLoader ? (
                            <div className="spin">
                                <Spin />
                            </div>
                        ) : (
                            <div className="overview-split">
                                <div className="score-section">
                                    <div>
                                        <div className="text">
                                            {t("total.score")}
                                        </div>
                                        <div className="big-score">
                                            {hasOverviewData ? displayScore : "—"}
                                        </div>
                                        <div className="rating-stars-mask">
                                            <div
                                                className="rating-stars"
                                                dir="ltr"
                                            >
                                                <style>
                                                    {`
                                        .star-container svg path {
                                            transform: scale(1.3);
                                        }
                                    `}
                                                </style>
                                                <StarRatings
                                                    starRatedColor="#d97706"
                                                    starEmptyColor="#e7e5e4"
                                                    rating={
                                                        hasOverviewData &&
                                                        overviewAverage != null
                                                            ? overviewAverage
                                                            : 0
                                                    }
                                                    starDimension="32px"
                                                    starSpacing="1px"
                                                    svgIconPath="M18.1359 1.48359C18.5216 0.821321 19.4784 0.821321 19.8641 1.48359L25.5671 11.2751C25.7084 11.5177 25.9452 11.6897 26.2196 11.7491L37.2942 14.1473C38.0432 14.3095 38.3389 15.2194 37.8282 15.7909L30.2783 24.2405C30.0913 24.4499 30.0008 24.7282 30.0291 25.0076L31.1705 36.2812C31.2477 37.0437 30.4737 37.6061 29.7724 37.297L19.4032 32.7277C19.1463 32.6145 18.8537 32.6145 18.5968 32.7277L8.22762 37.297C7.52628 37.6061 6.75226 37.0437 6.82946 36.2812L7.97091 25.0076C7.99919 24.7282 7.90875 24.4499 7.72169 24.2405L0.171763 15.7909C-0.338892 15.2194 -0.0432378 14.3095 0.705815 14.1473L11.7804 11.7491C12.0548 11.6897 12.2916 11.5177 12.4329 11.2751L18.1359 1.48359Z"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="caption">
                                            {Number(
                                                overview.feedback_total_count
                                            ) || 0}{" "}
                                            {t("review")}
                                        </div>
                                        <div className="caption">
                                            {hasOverviewData
                                                ? `${satisfactionPercentFromOverview(
                                                      overview
                                                  )} ${t("percent")}${t(
                                                      "satisfaction"
                                                  )}`
                                                : t("overview.no.reviews")}
                                        </div>
                                        <button
                                            className="report-button mt-2"
                                            onClick={() => setComingSoon(true)}
                                        >
                                            {t("satisfaction.details")}
                                        </button>
                                    </div>
                                </div>
                                {hasOverviewData ? (
                                    <div className="report-bar-container">
                                        {[5, 4, 3, 2, 1, 0].map((score) => (
                                            <ReportBar
                                                key={score}
                                                caption={score}
                                                value={
                                                    overview[
                                                        `feedback_score_${score}_count`
                                                    ]
                                                }
                                                percent={calculateOverviewPercent(
                                                    overview[
                                                        `feedback_score_${score}_count`
                                                    ]
                                                )}
                                                color={STAR_BAR_COLORS[score]}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="overview-empty">
                                        <p>{t("overview.no.reviews.hint")}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </ReportCard>
                <ReportCard className="trends-card">
                    <div className="barchart-header">
                        <h1 className="report-header">{t("trends")}</h1>
                        {perDay.length > 0 ? (
                            <div className="legend">
                                <div className="teal"></div>
                                <div>{t("feedback.counts")}</div>
                                <div className="coral"></div>
                                <div>{t("satisfaction.percent")}</div>
                            </div>
                        ) : null}
                    </div>
                    {perDayLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : perDay.length === 0 ? (
                        <ChartFallback setup={setup} variant="metrics" />
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <AreaChart data={perDay}>
                                <defs>
                                    <linearGradient
                                        id="reportCountFill"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#0f766e"
                                            stopOpacity={0.28}
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#0f766e"
                                            stopOpacity={0.02}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="reportScoreFill"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#c2410c"
                                            stopOpacity={0.2}
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#c2410c"
                                            stopOpacity={0.02}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 6"
                                    stroke="#ece7e1"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="datetime__date__j"
                                    domain={["dataMin", "dataMax"]}
                                    padding={{ left: 10, right: 10 }}
                                    axisLine={false}
                                    tickLine={false}
                                    style={{ fontWeight: 500, fontSize: 11 }}
                                />
                                <YAxis
                                    yAxisId="count"
                                    axisLine={false}
                                    tickLine={false}
                                    width={28}
                                    style={{ fontWeight: 500, fontSize: 11 }}
                                />
                                <YAxis
                                    yAxisId="score"
                                    orientation="right"
                                    axisLine={false}
                                    tickLine={false}
                                    width={28}
                                    style={{ fontWeight: 500, fontSize: 11 }}
                                />
                                <Tooltip
                                    formatter={(value, name) => [
                                        name === "count"
                                            ? value
                                            : Math.round(value * 100) / 100,
                                        perDayTooltipCaption(name),
                                    ]}
                                />
                                <Area
                                    yAxisId="count"
                                    isAnimationActive={false}
                                    type="monotone"
                                    dataKey="count"
                                    stroke="#0f766e"
                                    strokeWidth={2.5}
                                    fill="url(#reportCountFill)"
                                    dot={false}
                                    activeDot={{ r: 4, fill: "#0f766e" }}
                                />
                                <Area
                                    yAxisId="score"
                                    isAnimationActive={false}
                                    type="monotone"
                                    dataKey="avg_score"
                                    stroke="#c2410c"
                                    strokeWidth={2.5}
                                    fill="url(#reportScoreFill)"
                                    dot={false}
                                    activeDot={{ r: 4, fill: "#c2410c" }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </ReportCard>
            </div>
            <div className="focus-row">
                <ReportCard className="pipeline-card">
                    <h1 className="report-header">{t("pipeline.status")}</h1>
                    {feedbackLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : (
                        <>
                            <div className="pipeline-track" aria-hidden="true">
                                {pipeline.map((item) =>
                                    item.value ? (
                                        <span
                                            key={item.key}
                                            className="pipeline-seg"
                                            style={{
                                                flexGrow: item.value,
                                                background: item.color,
                                            }}
                                        />
                                    ) : null
                                )}
                                {pipeline.every((item) => !item.value) ? (
                                    <span className="pipeline-empty" />
                                ) : null}
                            </div>
                            <ul className="pipeline-legend">
                                {pipeline.map((item) => (
                                    <li key={item.key}>
                                        <span
                                            className="swatch"
                                            style={{ background: item.color }}
                                        />
                                        <span className="pipeline-label">
                                            {item.label}
                                        </span>
                                        <strong className="tabular-nums">
                                            {item.value}
                                        </strong>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </ReportCard>
                <ReportCard className="bubble-card">
                    <div className="chart-header">
                        <h1 className="report-header">{t("bubble.header")}</h1>
                        <button
                            className="report-button"
                            onClick={() => setItemTypeTreeModalShow(true)}
                        >
                            {t("filter")}
                        </button>
                    </div>
                    {bubbleLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : !bubbleHasData ? (
                        <ChartFallback setup={setup} variant="metrics" />
                    ) : (
                        <ResponsiveContainer
                            width="100%"
                            height={240}
                            key={reRenderKey}
                        >
                            <ScatterChart
                                margin={{
                                    top: 16,
                                    right: 16,
                                    bottom: 8,
                                    left: -8,
                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 6"
                                    stroke="#ece7e1"
                                />
                                <XAxis
                                    type="number"
                                    dataKey="feedback_count"
                                    name="feedback_count"
                                    padding={{ left: 24, right: 24 }}
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: t("feedback.count"),
                                        position: "insideBottomRight",
                                        offset: -4,
                                        style: {
                                            fontWeight: 600,
                                            fontSize: 11,
                                            fill: "#78716c",
                                        },
                                    }}
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 11,
                                    }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="computed_score"
                                    name="computed_score"
                                    axisLine={false}
                                    tickLine={false}
                                    label={{
                                        value: t("score"),
                                        angle: -90,
                                        position: "insideTop",
                                        style: {
                                            fontWeight: 600,
                                            fontSize: 11,
                                            fill: "#78716c",
                                        },
                                    }}
                                    padding={{ bottom: 24, top: 24 }}
                                    tickMargin={8}
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 11,
                                    }}
                                />
                                <ZAxis
                                    type="number"
                                    dataKey="exeWeight"
                                    name="weight"
                                    range={[400, 2800]}
                                />
                                <Tooltip
                                    cursor={{ strokeDasharray: "3 3" }}
                                    content={({ active, payload }) => {
                                        if (!active) return null;
                                        return (
                                            <div className="bubble-chart-tooltip">
                                                <div>
                                                    {t("category")}:{" "}
                                                    {payload[0].payload.name}
                                                </div>
                                                <div>
                                                    {t("score")}:{" "}
                                                    {Math.round(
                                                        payload[0].payload
                                                            .computed_score *
                                                            100
                                                    ) / 100}
                                                </div>
                                                <div>
                                                    {t("weight")}:{" "}
                                                    {payload[0].payload.weight}
                                                </div>
                                                <div>
                                                    {t("feedback.count")}:{" "}
                                                    {
                                                        payload[0].payload
                                                            .feedback_count
                                                    }
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                <Scatter
                                    isAnimationActive={false}
                                    data={bubble.filter(
                                        (item) =>
                                            item.weight >= 50 &&
                                            item.feedback_count > 0
                                    )}
                                    fill="#0f766eaa"
                                    stroke="#fff"
                                    strokeWidth={2}
                                />
                                <Scatter
                                    isAnimationActive={false}
                                    data={bubble.filter(
                                        (item) =>
                                            item.weight < 50 &&
                                            item.feedback_count > 0
                                    )}
                                    fill="#c2410caa"
                                    stroke="#fff"
                                    strokeWidth={2}
                                />
                            </ScatterChart>
                        </ResponsiveContainer>
                    )}
                </ReportCard>
            </div>
            <div className="middle-layout">
                <ReportCard style={{ width: "100%", minHeight: 360 }}>
                    <div className="chart-header">
                        <h1 className="report-header">
                            {t("itemtype.performance")}
                        </h1>
                    </div>
                    {itemRankingLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : itemRanking.length === 0 ? (
                        <ChartFallback
                            setup={setup}
                            variant="checklist"
                            stageIds={[
                                "itemtypes",
                                "items",
                                "forms",
                                "handling",
                            ]}
                        />
                    ) : (
                        <>
                            <div className="vertical-bar-header">
                                <div>{t("best.performances")}</div>
                                <div>{t("worst.performances")}</div>
                            </div>
                            <ResponsiveContainer
                                width="100%"
                                height={270}
                                key={reRenderKey}
                            >
                                <BarChart
                                    layout="vertical"
                                    data={itemRanking}
                                    stackOffset="sign"
                                    barGap={BAR_GAP}
                                    barCategoryGap={BAR_GAP}
                                    width={300}
                                    height={150}
                                    style={{ marginTop: -8, direction: "ltr" }}
                                >
                                    <Bar
                                        isAnimationActive={false}
                                        fill="#0f766e"
                                        dataKey="best"
                                        onMouseOver={() => setBestWorst("best")}
                                        label={(e) =>
                                            e.width != 0 && (
                                                <text
                                                    x={e.x}
                                                    y={e.y}
                                                    dx={e.width + 8}
                                                    dy={e.height / 2 + 4}
                                                    fontSize={10}
                                                    textAnchor="start"
                                                >
                                                    {
                                                        itemRanking[e.name]
                                                            .best_name
                                                    }
                                                </text>
                                            )
                                        }
                                        stackId="1"
                                    />
                                    <Bar
                                        isAnimationActive={false}
                                        onMouseOver={() =>
                                            setBestWorst("worst")
                                        }
                                        fill="#c2410c"
                                        dataKey="worst"
                                        label={(e) =>
                                            true && (
                                                <text
                                                    x={e.x}
                                                    y={e.y}
                                                    dx={e.width - 8}
                                                    dy={e.height / 2 + 4}
                                                    fontSize={10}
                                                    textAnchor="end"
                                                >
                                                    {
                                                        itemRanking[e.name]
                                                            .worst_name
                                                    }
                                                </text>
                                            )
                                        }
                                        stackId="1"
                                    />
                                    <XAxis
                                        hide
                                        type="number"
                                        dataKey="best"
                                        name="best"
                                        domain={[-10, 10]}
                                        label={{
                                            value: t("feedback.count"),
                                            position: "right",
                                            style: {
                                                fontWeight: 600,
                                                fontSize: 12,
                                            },
                                        }}
                                        style={{
                                            fontWeight: 500,
                                            fontSize: 12,
                                        }}
                                    />
                                    <ReferenceLine x={0} stroke="black" />
                                    <YAxis
                                        hide
                                        type="category"
                                        dataKey="name"
                                        name="computed_score"
                                        label={{
                                            value: t("score"),
                                            angle: -90,
                                            position: "insideTop",
                                            style: {
                                                fontWeight: 600,
                                                fontSize: 12,
                                            },
                                        }}
                                        tickMargin={16}
                                        style={{
                                            fontWeight: 500,
                                            fontSize: 12,
                                        }}
                                    />
                                    <Tooltip
                                        cursor={false}
                                        content={({ active, payload }) => {
                                            if (!active) return null;
                                            if (bestWorst === "best") {
                                                return (
                                                    <div className="vertical-barchart-tooltip">
                                                        <div>
                                                            {t("category")}:{" "}
                                                            {
                                                                payload[0]
                                                                    .payload
                                                                    .best_name
                                                            }
                                                        </div>
                                                        <div>
                                                            {t("score")}:{" "}
                                                            {Math.round(
                                                                payload[0]
                                                                    .payload
                                                                    .best * 100
                                                            ) / 100}
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div className="vertical-barchart-tooltip">
                                                        <div>
                                                            {t("category")}:{" "}
                                                            {
                                                                payload[0]
                                                                    .payload
                                                                    .worst_name
                                                            }
                                                        </div>
                                                        <div>
                                                            {t("score")}:{" "}
                                                            {Math.round(
                                                                (payload[0]
                                                                    .payload
                                                                    .worst +
                                                                    1) *
                                                                    -100
                                                            ) / 100}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    )}
                </ReportCard>
            </div>
            <div className="footer-layout">
                <ReportCard style={{ width: "100%", minHeight: 280 }}>
                    <div className="chart-header">
                        <h1 className="report-header">{t("best.criterias")}</h1>
                    </div>
                    {criteriaLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : !criteria.best?.length ? (
                        <ChartFallback
                            setup={setup}
                            variant="checklist"
                            stageIds={["forms", "surveys"]}
                        />
                    ) : (
                        <div className="criteria-bar-con">
                            {criteria.best?.map((r) => (
                                <div className="criteria-bar">
                                    <div>{r.criteria__criteria__name}</div>
                                    <div>
                                        {Math.round((r.avg / 5) * 10000) / 100}%
                                    </div>
                                    <div
                                        className="best"
                                        style={{
                                            width: (r.avg / 5) * 100 + "%",
                                        }}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    )}
                </ReportCard>
                <ReportCard
                    style={{ width: "100%", minHeight: 280 }}
                >
                    <div className="chart-header">
                        <h1 className="report-header">
                            {t("worst.criterias")}
                        </h1>
                    </div>
                    {criteriaLoader ? (
                        <div className="spin">
                            <Spin />
                        </div>
                    ) : !criteria.worst?.length ? (
                        <ChartFallback
                            setup={setup}
                            variant="participation"
                            stageIds={["participation", "surveys", "profile"]}
                        />
                    ) : (
                        <div className="criteria-bar-con">
                            {criteria.worst?.map((r) => (
                                <div className="criteria-bar">
                                    <div>{r.criteria__criteria__name}</div>
                                    <div>
                                        {Math.round((r.avg / 5) * 10000) / 100}%
                                    </div>
                                    <div
                                        className="worst"
                                        style={{
                                            width: (r.avg / 5) * 100 + "%",
                                        }}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    )}
                </ReportCard>
            </div>
            <CriteriaScoreBreakdown />
                    </Tab>
                    <Tab
                        eventKey="ai"
                        className="panel_tab_item"
                        title={t("reports.tab.ai")}
                    >
                        <AiSuggestedActions setup={setup} embedded />
                    </Tab>
                </Tabs>
                    </div>
                </div>
            </div>
        </div>
    );
}
